import {
  type V2AdminDocumentCategoriesDto,
  type V2CopyGalleryResultDto,
  type V2DocumentCategoryResultDto,
  type V2DocumentVisibilityDto,
  type V2GallerySourcesDto,
  type V2InheritedDocumentsDto,
  type V2OwnedDocumentsDto,
  type V2UploadedDocumentDto,
  v2AdminDocumentCategoriesControllerList,
  v2AdminDocumentsControllerCopyGallery,
  v2AdminDocumentsControllerDownloadUrl,
  v2AdminDocumentsControllerGallerySources,
  v2AdminDocumentsControllerInherited,
  v2AdminDocumentsControllerList,
  v2AdminDocumentsControllerPreviewUrl,
  v2AdminDocumentsControllerRemove,
  v2AdminDocumentsControllerRename,
  v2AdminDocumentsControllerReorderSlot,
  v2AdminDocumentsControllerSetCategory,
  v2AdminDocumentsControllerSetSlot,
  v2AdminDocumentsControllerSetVisibility,
  v2AdminDocumentsControllerThumbnailUrl,
  v2AdminDocumentsControllerUpload
} from '@idc-forms/api-client'
import { V2AdminDocumentCategoriesSchema } from '@idc-forms/schemas/api-v2/admin-document-categories'
import {
  V2CopyGalleryResultSchema,
  V2DocumentDownloadUrlSchema,
  V2DocumentNameResultSchema,
  V2DocumentPreviewUrlSchema,
  V2DocumentThumbnailUrlSchema,
  V2GallerySourcesSchema,
  V2InheritedDocumentsSchema,
  V2OwnedDocumentsSchema
} from '@idc-forms/schemas/api-v2/admin-documents'
import { assertShape } from '@/shared/api/assert-shape'
import { bearer, v2RequestOptions } from '@/shared/api/v2-request'
import type { DocumentOwnerType } from '../models/document-owner'

/**
 * El cartapacio contra la API v2 (ADR 0022). Cuatro llamadas y ninguna más: listar lo
 * que un dueño posee, subirle un archivo y publicar o despublicar uno.
 *
 * **Subir y publicar son dos actos distintos** y por eso son dos métodos. Todo nace
 * `internal`; publicar tiene permiso propio (`PublishCatalogDocuments`) porque manda
 * el archivo al teléfono de cada cliente del nivel. Fundirlos en un `upload({ public })`
 * volvería decorativo el segundo permiso.
 *
 * `v2RequestOptions` es OBLIGATORIO en cada llamada: trae baseUrl, cookies y el
 * `querySerializer` del contrato.
 */

/** Una fila del cartapacio: lo que ese dueño tiene subido. */
export type OwnedDocumentRow = V2OwnedDocumentsDto['data'][number]

/** Una fila HEREDADA: publicada por un ancestro de la cadena, de sólo lectura acá. */
export type InheritedDocumentApiRow = V2InheritedDocumentsDto['data'][number]

export type DocumentVisibility = OwnedDocumentRow['visibility']

export type UploadDocumentArgs = {
  ownerType: DocumentOwnerType
  ownerId: string
  file: File
  /** Nombre visible. Si falta, el backend usa el del archivo. */
  name?: string
  comment?: string
  /** La categoría (`cat_`) bajo la que el cliente lo verá. Ausente = sin clasificar. */
  categoryId?: string
  /** Join key estable del checklist; Créditos usa `<stage>.<item>.<field>`. */
  requirementId?: string
}

/** Una categoría de la plantilla global. */
export type DocumentCategoryRow = V2AdminDocumentCategoriesDto['data'][number]

/** Un dueño del que se puede copiar la galería, con su conteo y su portada. */
export type GallerySourceRow = V2GallerySourcesDto['data'][number]

/** Qué hace la copia con lo que el destino ya tenía en la galería. */
export type CopyGalleryMode = 'append' | 'replace'

export const documentsAdminV2Service = {
  /**
   * La plantilla global de categorías, que es el eje por el que agrupa la galería del cliente.
   *
   * Devuelve TAMBIÉN las deprecadas: el curador necesita verlas para mover su contenido. Quien
   * arma un selector debe filtrarlas — ofrecer una categoría dada de baja es invitar a llenarla.
   */
  listCategories: async (accessToken: string): Promise<DocumentCategoryRow[]> => {
    const result = await v2AdminDocumentCategoriesControllerList({
      ...v2RequestOptions,
      headers: bearer(accessToken)
    })
    return assertShape(V2AdminDocumentCategoriesSchema, result.data).data
  },
  /**
   * Lo que ese dueño POSEE, no el árbol resuelto. Lo heredado se lee en el expediente
   * y en la app; mezclarlo acá invitaría a "borrar" desde un proyecto algo que vive en
   * la sociedad.
   */
  listByOwner: async (
    accessToken: string,
    ownerType: DocumentOwnerType,
    ownerId: string
  ): Promise<OwnedDocumentRow[]> => {
    const result = await v2AdminDocumentsControllerList({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      query: { ownerType, ownerId }
    })
    return assertShape(V2OwnedDocumentsSchema, result.data).data
  },

  /**
   * La URL firmada para VER una imagen: la miniatura de la galería.
   *
   * Se pide de a UNA y no en el listado a propósito. Una URL firmada vence —quince minutos— y
   * meterla en la respuesta del listado ataría su vida a la de la lista: el cartapacio, que no
   * muestra imágenes, cargaría doce firmas que nadie va a usar, y la galería se quedaría con
   * URLs vencidas cada vez que una mutación cualquiera refresque el listado sin refrescarlas.
   *
   * Sólo firma IMÁGENES: un PDF es 400. No es un límite de la UI —lo explica el endpoint— sino
   * lo que sostiene que esta lectura no deje registro en la bitácora de descargas.
   */
  previewUrl: async (accessToken: string, id: string) => {
    const result = await v2AdminDocumentsControllerPreviewUrl({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id }
    })
    return assertShape(V2DocumentPreviewUrlSchema, result.data)
  },

  /**
   * La URL firmada para ABRIR o BAJAR el archivo — cualquier tipo, y con bitácora.
   *
   * Es la otra mitad del par de `previewUrl`, no su duplicado. Aquélla firma **sólo imágenes** y
   * no audita, y las dos cosas van juntas: si sirviera PDFs sería la forma de bajarse un
   * documento sin dejar rastro. Ésta audita, y por eso puede servir cualquier MIME.
   *
   * **Ver y descargar son la misma llamada.** Lo que cambia es el `Content-Disposition`, y lo
   * decide el servidor desde el tipo del archivo: un PDF se abre en la pestaña, una planilla se
   * ofrece para guardar. Del lado del navegador siempre se hace lo mismo — abrir la URL.
   *
   * Se pide de a UNA y en el momento del click, nunca en el listado: la firma vence en cinco
   * minutos y meterla en la lista ataría su vida a la de la lista.
   */
  downloadUrl: async (accessToken: string, id: string) => {
    const result = await v2AdminDocumentsControllerDownloadUrl({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id }
    })
    return assertShape(V2DocumentDownloadUrlSchema, result.data)
  },

  /**
   * Las hojas firmadas de la MINIATURA generada — el derivado en WebP, nunca el original.
   *
   * Es la otra puerta de imagen, y no el duplicado de `previewUrl`. Aquélla firma el ORIGINAL y
   * sólo si es una imagen: una foto de 35 megapíxeles para pintar 140 px, y un `400` para todo
   * PDF —que es de lo que está hecho el cartapacio—. Ésta sirve la hoja que el generador ya
   * produjo, así que vale igual para un plano escaneado y para un contrato.
   *
   * Devuelve HASTA DOS: la tarjeta dibuja la pila rotada, que es lo que distingue de un vistazo
   * un documento de varias páginas de uno de una sola. Vacío significa «no tiene» y no es un
   * error: la tarjeta pinta el arte por tipo de archivo y no vuelve a preguntar.
   *
   * Se pide de a UNA y sólo para las filas que el listado marcó con `hasThumbnail`: la firma
   * vence a los quince minutos, y meterla en el listado ataría su vida a la de la lista.
   */
  thumbnailUrl: async (accessToken: string, id: string) => {
    const result = await v2AdminDocumentsControllerThumbnailUrl({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id }
    })
    return assertShape(V2DocumentThumbnailUrlSchema, result.data)
  },

  /**
   * Lo que este nivel HEREDA de sus ancestros: la otra mitad de `listByOwner`.
   *
   * Aquélla devuelve lo que el dueño posee —y así debe ser, o se «borraría» desde un proyecto un
   * plano que vive en la sociedad—, pero la consecuencia era que el curador de un inmueble no
   * tenía forma de ver lo que su cliente sí ve. Esto es de SÓLO LECTURA: acá no hay acciones, y
   * esa ausencia es la que enseña que el origen se edita en el origen (ADR 0023).
   */
  listInherited: async (
    accessToken: string,
    ownerType: DocumentOwnerType,
    ownerId: string
  ): Promise<InheritedDocumentApiRow[]> => {
    const result = await v2AdminDocumentsControllerInherited({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      query: { ownerType, ownerId }
    })
    return assertShape(V2InheritedDocumentsSchema, result.data).data
  },

  /**
   * Renombra: sólo la etiqueta visible.
   *
   * Es ESCRITURA, así que el backend la pasa por las dos compuertas —el permiso del nivel y, si
   * el dueño es un expediente de crédito, el candado de estado—. Renombrar el adjunto de un
   * crédito aprobado es 409, igual que subirlo o borrarlo.
   */
  rename: async (accessToken: string, id: string, name: string) => {
    const result = await v2AdminDocumentsControllerRename({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id },
      body: { name }
    })
    return assertShape(V2DocumentNameResultSchema, result.data)
  },

  /** Sube. Nace SIEMPRE interno — la visibilidad no viaja acá ni puede hacerlo. */
  upload: async (accessToken: string, args: UploadDocumentArgs): Promise<V2UploadedDocumentDto> => {
    const result = await v2AdminDocumentsControllerUpload({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      body: {
        file: args.file,
        ownerType: args.ownerType,
        ownerId: args.ownerId,
        ...(args.name ? { name: args.name } : {}),
        ...(args.comment ? { comment: args.comment } : {}),
        ...(args.categoryId ? { categoryId: args.categoryId } : {}),
        ...(args.requirementId ? { requirementId: args.requirementId } : {})
      }
    })
    return result.data
  },

  /**
   * Borra: los bytes, la fila y su enlace a la carpeta.
   *
   * Es lo que hace que la política de permisos cierre — publicar quedó más restringido
   * que subir porque «un archivo subido por error se corrige borrándolo». Un documento
   * ya publicado pide además el permiso de publicar, y eso lo decide el backend.
   */
  remove: async (accessToken: string, id: string): Promise<void> => {
    await v2AdminDocumentsControllerRemove({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id }
    })
  },

  /**
   * Reclasifica. `null` lo saca de todo grupo, que es una acción legítima y no un valor faltante.
   *
   * Va aparte de `setVisibility` porque son cosas distintas: clasificar ordena, publicar es lo que
   * llega al teléfono del cliente. Mover algo YA publicado pide además el permiso de publicar, y
   * eso lo decide el backend.
   */
  setCategory: async (
    accessToken: string,
    id: string,
    categoryId: string | null
  ): Promise<V2DocumentCategoryResultDto> => {
    const result = await v2AdminDocumentsControllerSetCategory({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id },
      body: { categoryId }
    })
    return result.data
  },

  /** Publica o despublica. El backend descubre el dueño leyendo la fila, no se lo decimos. */
  setVisibility: async (
    accessToken: string,
    id: string,
    visibility: DocumentVisibility
  ): Promise<V2DocumentVisibilityDto> => {
    const result = await v2AdminDocumentsControllerSetVisibility({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id },
      body: { visibility }
    })
    return result.data
  },

  /**
   * Mete o saca un documento del slot `gallery`. `null` lo saca.
   *
   * NO publica: la foto sigue siendo interna hasta que alguien la publique aparte. Meter
   * algo al slot es acomodar el estante; publicarlo es lo que lo manda al teléfono.
   */
  setSlot: async (accessToken: string, id: string, slot: 'gallery' | null) => {
    const result = await v2AdminDocumentsControllerSetSlot({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id },
      body: { slot }
    })
    return result.data
  },

  /**
   * De dónde puede copiar su galería este dueño: la cadena por encima y los subtipos hermanos.
   *
   * Trae `photoCount` y `coverDocumentId` de cada candidato para que el diálogo se lea sin abrir
   * nada. La miniatura NO viene firmada: se pide con `previewUrl(coverDocumentId)`, que ya existe
   * y ya cachea por foto — firmarlas acá ataría su vigencia a la de esta lista.
   */
  listGallerySources: async (
    accessToken: string,
    ownerType: DocumentOwnerType,
    ownerId: string
  ): Promise<GallerySourceRow[]> => {
    const result = await v2AdminDocumentsControllerGallerySources({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      query: { ownerType, ownerId }
    })
    return assertShape(V2GallerySourcesSchema, result.data).data
  },

  /**
   * Copia la galería de otro dueño, SIN volver a subir los archivos.
   *
   * Las copias apuntan al mismo asset y al mismo path que el origen (ADR 0022), así que esto no
   * mueve un byte ni hace crecer el bucket. `append` suma detrás de lo que el destino ya tenía;
   * `replace` saca del slot —no borra— las propias del destino antes de sumar.
   *
   * **No publica.** Las copias conservan la visibilidad del origen sólo si quien copia puede
   * publicar en el destino; si no, entran internas. Lo decide el backend.
   */
  copyGallery: async (
    accessToken: string,
    input: {
      from: { ownerType: DocumentOwnerType; ownerId: string }
      to: { ownerType: DocumentOwnerType; ownerId: string }
      mode: CopyGalleryMode
    }
  ): Promise<V2CopyGalleryResultDto> => {
    const result = await v2AdminDocumentsControllerCopyGallery({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      body: input
    })
    return assertShape(V2CopyGalleryResultSchema, result.data)
  },

  /**
   * Reescribe el orden del slot ENTERO. La lista ES el slot: lo que no está, sale.
   *
   * Una sola llamada y no una por foto: arrastrar la octava al principio reordena ocho
   * filas, y hacerlo de a una deja estados intermedios que un cliente puede ver.
   */
  reorderSlot: async (
    accessToken: string,
    input: {
      ownerType: DocumentOwnerType
      ownerId: string
      slot: 'gallery'
      attachmentIds: string[]
    }
  ) => {
    const result = await v2AdminDocumentsControllerReorderSlot({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      body: input
    })
    return result.data
  }
}
